import { Paper, styled } from "~/common/mui";
import { NOTIFICATIONS_PAGE, STORE_FINDER, WISHLIST_PAGE } from "~/utils/url/staticUrls";
import { canSeeSalesFinder } from "~/features/navigation/components/headerMenu/navigationVisibility";
import { useDispatch, useSelector } from "react-redux";
import { useProductComparisonCount } from "~/features/compareProducts/api/productComparisonQuery";
import { getWishlistNavigationEntryUrl, useLoadWishlistsOnMount } from "~/features/wishlist/utils/wishlistHelper";
import { selectWishlistAmount } from "~/features/wishlist/redux/wishlistSelectors";
import { selectNewNotificationAmount } from "~/features/notifications/redux/NotificationsSelectors";
import { Permission, userHasAnyPermission, userHasPermission } from "~/domains/user/utils/permissions";
import { markNotificationsAsInteractedThunk } from "~/features/notifications/redux/actions";
import { useTranslation } from "react-i18next";
import { canSeeWishlistButton } from "~/utils/features";
import { MobileMinicart } from "~/features/cart/minicart/MinicartComponent";
import { useIsBottomNavigationVisible } from "~/features/BottomNavigation/BottomNavigationUtils";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CompareOutlinedIcon from "@mui/icons-material/CompareOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import { ComparisonDialogProvider } from "~/features/compareProducts/components/ComparisonDialogProvider";
import { useComparisonDialog } from "~/features/compareProducts/hooks/useComparisonDialog";
import { BottomNavigationItem } from "~/features/BottomNavigation/BottomNavigationItem";
import { userIsPublic } from "~/domains/user/utils/roles";
import { openAuthOverlay } from "../registration/components/redux/AuthActions";

export { BottomNavigationItem } from "~/features/BottomNavigation/BottomNavigationItem";

const BOTTOM_NAV_HEIGHT = 56;

const StyledPaper = styled(Paper)(({ theme }) => ({
  display: "flex",
  height: BOTTOM_NAV_HEIGHT,
  width: "100vw",
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  opacity: 0.92,
  background: theme.palette.surface.main,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderRadius: "0 !important",
  paddingBottom: 4,
  boxSizing: "border-box",
  zIndex: 1,
}));

const StyledList = styled("ul")({
  display: "inline-flex",
  justifyContent: "space-between",
  width: "100%",
  listStyleType: "none",
  margin: 0,
  padding: 0,
});

const StyledListItemContainer = styled("li")({
  flex: 1,
});

function BottomNavigationSection() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { openComparisonDialog } = useComparisonDialog();

  const { count: listItems } = useProductComparisonCount();

  const onComparisonClick = () => {
    if (userIsPublic()) {
      dispatch(openAuthOverlay("login"));
      return;
    }
    openComparisonDialog();
  };

  const showWishlistMenuEntry = canSeeWishlistButton();
  const wishlistUrl = getWishlistNavigationEntryUrl();
  useLoadWishlistsOnMount();
  const wishlistAmount = useSelector(selectWishlistAmount);

  const newNotificationAmount = useSelector(selectNewNotificationAmount);

  const canSeeCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);

  if (!useIsBottomNavigationVisible()) {
    return null;
  }

  const onClick = (e: React.MouseEvent) => {
    if (userIsPublic()) {
      e.preventDefault();
      dispatch(openAuthOverlay("login"));
    }
  };

  return (
    <StyledPaper variant={"elevation"} elevation={3}>
      <StyledList>
        <StyledListItemContainer>
          <div id="navigation_comparison_list_btn" data-test-id={"navigation-wishlist"}>
            <BottomNavigationItem
              data-test-id={"navigation-btn-sales-partner"}
              quantity={listItems}
              icon={<CompareOutlinedIcon />}
              name={t("header.link.comparison.list")}
              onClick={onComparisonClick}
            />
          </div>
        </StyledListItemContainer>
        {showWishlistMenuEntry && (
          <StyledListItemContainer>
            <a
              href={userIsPublic() ? undefined : wishlistUrl}
              id="navigation_wishlist_btn"
              data-test-id={"navigation-wishlist"}
              onClick={onClick}
            >
              <BottomNavigationItem
                name={t("wishlistPage.title")}
                icon={<BookmarkBorderOutlinedIcon />}
                data-test-id={"navigation-btn-wishlist"}
                quantity={wishlistAmount !== undefined ? wishlistAmount : ""}
                isActive={window.location.pathname.endsWith(WISHLIST_PAGE)}
              />
            </a>
          </StyledListItemContainer>
        )}
        {userHasPermission(Permission.SEE_NOTIFICATIONS) && (
          <StyledListItemContainer>
            <a href={NOTIFICATIONS_PAGE} id="navigation_notification_btn" data-test-id={"navigation-notification"}>
              <BottomNavigationItem
                name={t("header.link.my.notification")}
                data-test-id={"navigation-btn-notification"}
                icon={<NotificationsOutlinedIcon />}
                quantity={newNotificationAmount}
                onClick={() => dispatch(markNotificationsAsInteractedThunk())}
                isActive={window.location.pathname.endsWith(NOTIFICATIONS_PAGE)}
                isNotification
              />
            </a>
          </StyledListItemContainer>
        )}
        {canSeeSalesFinder() && (
          <StyledListItemContainer>
            <a href={STORE_FINDER} data-test-id={"navigation-sales-partner"}>
              <BottomNavigationItem
                data-test-id={"navigation-btn-sales-partner"}
                icon={<LocationOnOutlinedIcon />}
                name={t("salesPartner.title")}
                isActive={window.location.pathname.endsWith(STORE_FINDER)}
              />
            </a>
          </StyledListItemContainer>
        )}
        {canSeeCart && (
          <StyledListItemContainer>
            <MobileMinicart />
          </StyledListItemContainer>
        )}
      </StyledList>
    </StyledPaper>
  );
}

export function createBottomNavigation() {
  return (
    <ComparisonDialogProvider>
      <BottomNavigationSection />
    </ComparisonDialogProvider>
  );
}
